'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';

import { Spinner } from '@workspace/lib/components/atoms/icons/Spinner';

interface LoaderProps {
  textContent?: string;
  subText?: string;
  /**
   * Visual variant of the loader:
   * - 'overlay' (default): Full-screen white overlay with spinner
   * - 'holdingScreen': Centered bordered box with spinner (matches KYC holding screen)
   */
  variant?: 'overlay' | 'holdingScreen';
}

export function Loader({
  textContent,
  subText,
  variant = 'overlay',
}: LoaderProps) {
  const t = useTranslations();
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const loaderText = textContent
    ? t.has(textContent)
      ? t(textContent)
      : textContent
    : t('Common.loaderFallbackText');

  // Note: removes focus from active element and sets it to loader when loading status equal to 'true'
  useEffect(() => {
    // Note: save whatever element had focus before the loader appeared
    previouslyFocusedElement.current = document.activeElement as HTMLElement;

    // Note: move focus to the loader component
    if (loaderRef.current) {
      loaderRef.current.focus();
    }

    // Note: prevent background scrolling
    document.body.classList.add('overflow-hidden');

    // Note: when unmounting, restore focus and scroll
    return () => {
      document.body.classList.remove('overflow-hidden');
      previouslyFocusedElement.current?.focus();
    };
  }, []);

  if (variant === 'holdingScreen') {
    return (
      <div
        ref={loaderRef}
        className='fixed inset-0 z-50 flex items-center justify-center bg-white'
        role='status'
        aria-busy='true'
        tabIndex={-1}
      >
        <div className='w-full max-w-lg rounded-lg border border-neutral-600 px-4 py-10 text-center md:px-8'>
          <p className='text-primary-black text-lg font-semibold'>
            {loaderText}
          </p>
          {subText && (
            <p className='text-primary-black mt-1 text-lg font-semibold'>
              {t(subText)}
            </p>
          )}
          <div className='mt-6 flex justify-center'>
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={loaderRef}
      className='animate-show-loader fixed inset-0 z-50 flex items-center justify-center bg-white/90'
      role='status'
      aria-busy='true'
      tabIndex={-1}
    >
      <div className='flex flex-col items-center gap-6'>
        <p className='font-branding max-w-[295px] text-center text-[20px] leading-[120%] font-semibold text-black md:max-w-[360px]'>
          {loaderText}
        </p>
        <Spinner size='sm' className='lg:hidden' />
        <Spinner size='lg' className='hidden lg:block' />
        {subText && (
          <p className='font-nunito max-w-[260px] text-center text-base leading-[135%] font-normal text-black'>
            {t(subText)}
          </p>
        )}
      </div>
    </div>
  );
}
